/**
 * Helpers for showing an email message as the reader wrote it: the message
 * body only, without the quoted history that mail clients append to replies.
 * Pure string/DOM work; sanitisation for rendering stays in the caller.
 */

const HTML_TAG_RE = /<\/?(p|div|br|span|a|table|blockquote|ul|ol|li|h[1-6]|strong|em|b|i|img)\b[^>]*>/i;

/** True when a "plain" body is actually HTML markup (campaign content is stored that way). */
export function looksLikeHtml(text: string): boolean {
  return HTML_TAG_RE.test(text);
}

// Where a reply's own text ends and the quoted original begins, per client.
const TEXT_QUOTE_MARKERS: RegExp[] = [
  /^On .{0,300}?wrote:\s*$/m,                 // Gmail, Apple Mail
  /^-{2,}\s*Original Message\s*-{2,}\s*$/mi,  // Outlook (classic)
  /^_{5,}\s*$/m,                              // Outlook divider line
  /^From:\s.+\n(Sent|Date):\s.+$/m,           // Outlook header block
  /^Le .{0,200}? a écrit\s*:\s*$/m,           // French clients
  /^Am .{0,200}? schrieb .{0,100}?:\s*$/m,    // German clients
  /^El .{0,200}? escribió:\s*$/m,             // Spanish clients
  /^\s*>/m,                                   // first quoted line
];

/** Returns the reply text above the quoted history. Falls back to the input when nothing is left. */
export function stripQuotedText(text: string): string {
  let normalised = text.replace(/\r\n?/g, '\n');
  // Gmail wraps a long attribution line; rejoin "On <date>\n<name> wrote:".
  normalised = normalised.replace(/^(On [^\n]{0,200})\n([^\n]{0,200} wrote:)\s*$/m, '$1 $2');

  let cut = normalised.length;
  for (const marker of TEXT_QUOTE_MARKERS) {
    const match = marker.exec(normalised);
    if (match && match.index < cut) cut = match.index;
  }

  const stripped = normalised.slice(0, cut).trim();
  return stripped.length > 0 ? stripped : normalised.trim();
}

// Elements that hold quoted history in the common webmail/desktop clients.
const QUOTE_SELECTORS = [
  'blockquote.gmail_quote',
  'div.gmail_quote',
  '.gmail_quote_container',
  '.gmail_extra',
  '.gmail_attr',
  '#divRplyFwdMsg',
  '[id^="divRplyFwdMsg"]',
  '[id^="x_divRplyFwdMsg"]',
  '#appendonsend',
  '.yahoo_quoted',
  'blockquote[type="cite"]',
  '.moz-cite-prefix',
  '.protonmail_quote',
].join(',');

const OUTLOOK_TRAILING_QUOTE = /divRplyFwdMsg|appendonsend/i;

/**
 * Removes quoted history from an HTML email body and returns the remaining
 * markup. Parsing goes through DOMParser, which is inert (no script execution,
 * no resource loads), so this is safe on untrusted input; the caller still
 * sanitises the result before rendering.
 */
export function stripQuotedHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const root = doc.body;

  root.querySelectorAll(QUOTE_SELECTORS).forEach((el) => {
    // Outlook puts the original message after a header div rather than inside it.
    if (OUTLOOK_TRAILING_QUOTE.test(el.id)) {
      let sibling = el.nextSibling;
      while (sibling) {
        const next = sibling.nextSibling;
        sibling.parentNode?.removeChild(sibling);
        sibling = next;
      }
    }
    el.remove();
  });

  // Generic fallback: a blockquote introduced by an "… wrote:" line.
  root.querySelectorAll('blockquote').forEach((quote) => {
    const intro = quote.previousElementSibling;
    if (intro && /wrote:\s*$/.test(intro.textContent || '')) {
      intro.remove();
      quote.remove();
    }
  });

  // Drop the empty lines clients leave above the quote.
  while (root.lastElementChild) {
    const last = root.lastElementChild;
    const isBlank = last.tagName === 'BR' || (!(last.textContent || '').trim() && !last.querySelector('img'));
    if (!isBlank) break;
    last.remove();
  }

  const remaining = root.innerHTML.trim();
  return remaining.length > 0 ? remaining : html;
}

export type MessageDisplay =
  | { kind: 'html'; html: string }
  | { kind: 'text'; text: string }
  | { kind: 'empty' };

/** Picks the best representation of a message body and strips quoted history from it. */
export function getMessageDisplay(body: string | null | undefined, htmlBody: string | null | undefined): MessageDisplay {
  const html = (htmlBody || '').trim();
  if (html && html.replace(/<[^>]*>/g, '').trim().length > 0) {
    return { kind: 'html', html: stripQuotedHtml(html) };
  }

  const text = (body || '').trim();
  if (!text) return { kind: 'empty' };
  if (looksLikeHtml(text)) return { kind: 'html', html: stripQuotedHtml(text) };
  return { kind: 'text', text: stripQuotedText(text) };
}
